import { slugForType } from '@/features/integrations/display-taxonomy'

/**
 * Where "Add integration" goes from every Network Collectors surface: the shared
 * catalog, on its **Network Logs** tab.
 *
 * One catalog, diverging at connect — browsing Network Logs from here is the same
 * act as opening the catalog and clicking the tab, so this is a link into the
 * existing surface rather than a second inline picker. Declared once because the
 * call sites (the list header, each expanded Collector's source panel, and the
 * detail page's header and empty state) present it differently and must not
 * drift to different destinations.
 *
 * **It carries no Collector, and does not need to.** D13 decided this
 * destination for the *list*, where there is no Collector in scope; the detail
 * page and its empty state reuse it. A Log Source cannot exist without a
 * Collector, so the binding has to happen somewhere — and ticket 08 put it on
 * the Register step's own **Receive on collector** select, which every entry
 * point reaches identically. So there is nothing for this link to carry: the
 * Collector is chosen where the source is described, not on the way there.
 *
 * The slug comes from `slugForType`, the one owner of the type→slug mapping the
 * catalog reads back — a literal `'network-log'` here would be a second naming
 * scheme to keep in sync (ADR 0018 §4).
 */
export const networkLogCatalogLink = {
	to: '/integrations/new',
	search: { type: slugForType('Network Log') },
} as const

/**
 * Where a **Network Logs** catalog card goes: the Add Log Source wizard, for
 * that Log Source Type.
 *
 * Not `/integrations/new/$id`, which every Cloud card uses — that route runs the
 * server-driven connector setup form, and a Log Source has no connector to set
 * up. It has a listener and a Collector, which is a different screen (D12: one
 * catalog, diverging at connect).
 *
 * Declared here beside the catalog link because the two are the same journey's
 * two halves, and a second spelling of this destination would be a second place
 * for the route to drift.
 */
export function networkLogWizardLink(definitionId: string) {
	return {
		to: '/integrations/collectors/sources/new/$definitionId',
		params: { definitionId },
	} as const
}
